import { PATHS } from "../../lib/routes.js";
import {
  CalendarDays,
  Eye,
  FileDown,
  FileText,
  KeyRound,
  LayoutDashboard,
  type LucideIcon,
  Plug,
  RefreshCw,
  Search,
  Split,
  Target,
  Telescope,
  Users,
} from "../icons.js";
import type { ArtVariant } from "../pixel-art/palette.js";
import type { SceneName } from "../pixel-art/scenes.js";

/** One thing that shipped, as the What's new modal tells it (DESIGN.md §9). */
export interface Update {
  /** Stable, and newest-first order matters: "seen" remembers the newest id. */
  readonly id: string;
  /** The day it reached customers, `YYYY-MM-DD`. */
  readonly day: string;
  readonly title: string;
  /** One sentence: what it does for the reader. */
  readonly lede: string;
  readonly points: readonly { readonly icon: LucideIcon; readonly text: string }[];
  /** A film of it from the landing page, by name, or none. */
  readonly film: "crux-hero" | "crux-mcp" | "crux-research" | null;
  readonly art: { readonly variant: ArtVariant; readonly scene: SceneName };
  /** Where the last step's button goes, and whether only an owner or admin may. */
  readonly action: {
    readonly label: string;
    readonly path: string;
    readonly managersOnly: boolean;
  } | null;
}

/**
 * Newest first. Every claim here is checked against what shipped: the page
 * study's limit is `MAX_STUDY_PAGES`, the app sign-in is OAuth from claude.ai
 * and ChatGPT, and the export is the Brief's PDF with the parts its dialog
 * names. A page's rounds are `ROUND_SIZE`, `MAX_DEEP_PROMPTS` and
 * `FOUND_ENOUGH` in the API's `page_studies/round_rules.rs`.
 */
export const UPDATES: readonly Update[] = [
  {
    id: "2026-10-15-new-crux",
    day: "2026-10-15",
    title: "A new Crux",
    lede: "Every screen, rebuilt. One brand at a time, your dates and engines in the top bar, and every question on a page of its own.",
    points: [
      { icon: LayoutDashboard, text: "The Brief: where you stand, in one line" },
      { icon: Target, text: "Opportunities: what to do next, in one list" },
      { icon: Telescope, text: "Find questions: keywords, your brand or a page" },
    ],
    film: "crux-hero",
    art: { variant: "dusk", scene: "spark" },
    action: {
      label: "See your opportunities",
      path: PATHS.opportunities,
      managersOnly: false,
    },
  },
  {
    id: "2026-10-15-page-rounds",
    day: "2026-10-15",
    title: "A page, searched until it is found",
    lede: "Questions for a page no longer asks how many. Crux asks in rounds, each written from what the last one found, until the questions that cite your page turn up, or 100 have been asked.",
    points: [
      { icon: RefreshCw, text: "Rounds of 12 questions" },
      { icon: Search, text: "Stops at 5 that cite the page" },
      { icon: FileText, text: "Never more than 100 a page" },
    ],
    film: null,
    art: { variant: "ember", scene: "drift" },
    action: {
      label: "Find questions for a page",
      path: `${PATHS.research}?m=page`,
      managersOnly: false,
    },
  },
  {
    // One entry for AI apps, first shipped 2026-10-11 and opened to everyone on
    // 2026-10-15. It keeps its first id: "seen" stores the newest id a person
    // opened, and this was the newest until the revamp, so a new id here would
    // mark every update unseen again for them.
    id: "2026-10-11-ai-apps",
    day: "2026-10-15",
    title: "Crux, inside your AI apps",
    lede: "Ask Claude, ChatGPT or Cursor how your brands are doing. Anyone can connect one now, and one connection reaches every workspace you are in — never more than you can see there.",
    points: [
      { icon: Plug, text: "Sign in from Claude or ChatGPT" },
      { icon: KeyRound, text: "A key for Claude Code and Cursor" },
      { icon: Users, text: "Anyone, across all your workspaces" },
    ],
    film: "crux-mcp",
    art: { variant: "dusk", scene: "bloom" },
    action: { label: "Connect an app", path: PATHS.aiApps, managersOnly: false },
  },
  {
    id: "2026-10-09-page-questions",
    day: "2026-10-09",
    title: "Questions for a page",
    lede: "Paste up to five pages. Crux writes the questions each one should answer, asks the engines, and shows which questions cite it.",
    points: [
      { icon: FileText, text: "Up to five pages at once" },
      { icon: Split, text: "Every engine you pick, asked once" },
      { icon: Search, text: "The questions that cite each page" },
    ],
    film: "crux-research",
    art: { variant: "ember", scene: "drift" },
    action: {
      label: "Find questions for a page",
      path: `${PATHS.research}?m=page`,
      managersOnly: false,
    },
  },
  {
    id: "2026-10-09-export",
    day: "2026-10-09",
    title: "A report you can send",
    lede: "Export a brand's Brief as a PDF — its coverage, ranking, questions, citations and sources — for the dates you pick.",
    points: [
      { icon: FileDown, text: "One file, ready to send" },
      { icon: CalendarDays, text: "Any date range you pick" },
      { icon: Eye, text: "The same numbers you see on screen" },
    ],
    film: null,
    art: { variant: "sky", scene: "spark" },
    action: null,
  },
];
